import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import OrderConfirmedHero from "./components/OrderConfirmedHero";
import OrderConfirmedItems from "./components/OrderConfirmedItems";
import OrderConfirmedPriceBreakdown from "./components/OrderConfirmedPriceBreakdown";
import OrderConfirmedInfoGrid from "./components/OrderConfirmedInfoGrid";
import TrustBadgesFooter from "../../components/TrustBadgesFooter";
import { fetchAndDownloadInvoice } from "../../utils/invoiceGenerator";
import useStore from "../../store/useStore";

const OrderConfirmed = ({ order }) => {
  const navigate = useNavigate();
  const { cart_meta } = useStore();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [order?.order_id, order?.id]);

  // Dynamic delivery options lookup from API
  const deliveryOptions = cart_meta?.delivery_options || [];
  const rawDeliveryType = String(order?.delivery_type || "STANDARD").toUpperCase();

  const matchedOpt = deliveryOptions.find(
    (o) => String(o.type).toUpperCase() === rawDeliveryType
  );

  // Always start from the actual fee stored on the order (the ground truth).
  // Only override if the live delivery_options API has an explicit fee for this type.
  let dynamicFee = parseFloat(order?.delivery_fee ?? 0);
  if (matchedOpt && matchedOpt.fee != null) {
    dynamicFee = Number(matchedOpt.fee);
  }

  const subtotal = parseFloat(order?.subtotal ?? 0);
  const tax = parseFloat(order?.tax_amount ?? 0);
  const discount = parseFloat(order?.discount_amount ?? 0);
  const calculatedTotal = subtotal + tax + dynamicFee - discount;
  const grandTotal = calculatedTotal > 0 ? calculatedTotal : parseFloat(order?.total_amount ?? order?.total ?? 0);

  const resolvedOrder = {
    ...order,
    delivery_type: rawDeliveryType,
    delivery_fee: dynamicFee,
    total_amount: grandTotal,
    total: grandTotal,
  };

  const items = resolvedOrder?.items || resolvedOrder?.order_items || [];
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const handleDownloadInvoice = async () => {
    const orderId = resolvedOrder?.order_id ?? resolvedOrder?.id ?? order?.order_id ?? order?.id;
    if (!orderId) {
      toast.error("Order ID not found for invoice");
      return;
    }
    if (downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      await fetchAndDownloadInvoice(orderId);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleChatSupport = () => {
    navigate("/contact");
  };

  const handleViewOrderDetails = () => {
    if (order?.order_id) {
      navigate(`/account/orders`);
    } else {
      navigate("/account/orders");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-20 pt-3 sm:pt-6 font-sans">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 space-y-3 sm:space-y-5">

        {/* Step Tracker Progress — compact on mobile */}
        <div className="w-full py-2 px-3 sm:py-3 sm:px-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-between text-[11px] sm:text-sm font-semibold">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[#046a38]">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#046a38] text-white text-[10px] font-bold">
              1
            </span>
            <span className="hidden sm:inline font-bold">Cart / Review</span>
            <span className="sm:hidden font-semibold text-[10px]">Cart</span>
          </div>
          <div className="h-px flex-1 mx-1.5 sm:mx-3 bg-[#046a38]" />

          <div className="flex items-center gap-1.5 sm:gap-2 text-[#046a38]">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#046a38] text-white text-[10px] font-bold">
              2
            </span>
            <span className="hidden sm:inline font-bold">Checkout</span>
            <span className="sm:hidden font-semibold text-[10px]">Pay</span>
          </div>
          <div className="h-px flex-1 mx-1.5 sm:mx-3 bg-[#046a38]" />

          <div className="flex items-center gap-1.5 sm:gap-2 bg-[#046a38] text-white px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full font-bold shadow-sm">
            <span className="flex h-4 w-4 sm:h-5 sm:w-5 shrink-0 items-center justify-center rounded-full bg-white text-[#046a38] text-[10px] font-bold">
              3
            </span>
            <span className="text-[10px] sm:text-sm">Placed ✓</span>
          </div>
        </div>

        {/* 1. Hero Confirmation Card */}
        <OrderConfirmedHero order={resolvedOrder} />

        {/* 2. Main 2-Column Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <OrderConfirmedItems
              items={items}
              order={resolvedOrder}
              onViewDetails={handleViewOrderDetails}
            />
          </div>

          <div className="lg:col-span-1">
            <OrderConfirmedPriceBreakdown order={resolvedOrder} />
          </div>
        </div>

        {/* 3. 4-Card Info Grid */}
        <OrderConfirmedInfoGrid
          order={resolvedOrder}
          onDownloadInvoice={handleDownloadInvoice}
          onChatSupport={handleChatSupport}
        />

        {/* 4. Continue Shopping Action — full-width vertical stack on mobile */}
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-center sm:gap-3 pt-1">
          <button
            onClick={handleDownloadInvoice}
            disabled={downloadingInvoice}
            className="w-full sm:w-auto rounded-xl border-2 border-[#046a38] bg-white px-6 py-3 sm:py-3.5 text-sm font-bold text-[#046a38] hover:bg-emerald-50 transition active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 sm:min-w-[200px]"
          >
            {downloadingInvoice ? (
              <svg className="h-4 w-4 shrink-0 animate-spin text-[#046a38]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a1 1 0 001 1h16a1 1 0 001-1v-3M3 17H1m2 0h18m0 0h2" />
              </svg>
            )}
            {downloadingInvoice ? "Fetching Invoice..." : "Download Invoice"}
          </button>
          <button
            onClick={() => navigate("/products")}
            className="w-full sm:w-auto rounded-xl bg-[#046a38] px-6 py-3 sm:py-3.5 text-sm font-bold text-white shadow-md hover:bg-[#03522b] transition active:scale-95 sm:min-w-[200px]"
          >
            Continue Shopping
          </button>
          <button
            onClick={() => navigate("/account/orders")}
            className="w-full sm:w-auto rounded-xl border border-gray-300 bg-white px-6 py-3 sm:py-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition active:scale-95 sm:min-w-[200px]"
          >
            View All Orders
          </button>
        </div>

        {/* 5. Trust Badges Footer */}
        <TrustBadgesFooter />

      </div>
    </div>
  );
};

export default OrderConfirmed;
